import { useState } from 'react';
import type React from 'react';
import type { MouseEvent } from 'react';

import { EditRounded as EditIcon } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { OrdDatagrid, OrdIconStandard, OrdList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { UserCallRecordingConfFields, dataSchema } from '@ragu-components/ord-types';
import {
  DeleteWithConfirmButton,
  FunctionField,
  ResourceContextProvider,
  WrapperField,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
} from 'react-admin';

import type { OrdListProps } from '@ragu-components/ord-components';
import type { UserCallRecordingConf } from '@ragu-components/ord-types';

const RECORDING_TYPES = dataSchema.UserCallRecordingConfCreate.properties.type.enum;

const EditTypeButton = ({ nestedResource }: { nestedResource: string }): React.ReactElement => {
  const record = useRecordContext<UserCallRecordingConf>();
  const notify = useNotify();
  const translate = useTranslate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [update, { isPending }] = useUpdate(nestedResource, undefined, {
    onSuccess: () => {
      notify('ra.notification.updated', { type: 'success', messageArgs: { smart_count: 1 } });
    },
    onError: () => {
      notify('ra.notification.http_error', { type: 'error' });
    },
  });

  if (record == null) return <></>;

  const handleOpen = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };

  const handleSelect = (type: string): void => {
    setAnchor(null);
    void update(nestedResource, { id: record.id, data: { type }, previousData: record });
  };

  return (
    <>
      <Tooltip title={translate('ra.action.edit')}>
        <span>
          <IconButton size='small' color='primary' onClick={handleOpen} disabled={isPending}>
            <EditIcon fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => {
          setAnchor(null);
        }}
      >
        {RECORDING_TYPES.map((t) => (
          <MenuItem
            key={t}
            selected={t === record.type}
            onClick={() => {
              handleSelect(t);
            }}
          >
            {translate(`resources.call_recording_confs.fields.types.${t}`, { _: t })}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const UserCallRecordingConfsEmbeddedList = ({
  confId,
  ...props
}: { confId: string | number } & Partial<OrdListProps>): React.ReactElement => {
  const translate = useTranslate();
  // Nested resource path — same pattern as call_sessions/{id}/call_session_events.
  const resource = `call_recording_confs/${String(confId)}/user_call_recording_confs`;

  return (
    <ResourceContextProvider value={Resources.call_recording_confs}>
      <OrdList
        resource={resource}
        title={translate('resources.user_call_recording_confs.name', { _: 'User Recording Assignments' })}
        icon={<OrdIconStandard name={'users'} />}
        hasCreate={false}
        hasFilters={false}
        disableAuthentication
        showTabTitle={false}
        sort={{ field: UserCallRecordingConfFields.type, order: 'ASC' }}
        {...props}
      >
        <OrdDatagrid bulkActionButtons={false} rowClick={false}>
          <FunctionField<UserCallRecordingConf>
            label='resources.user_call_recording_confs.fields.user'
            sortable={false}
            render={(record) =>
              [record.user.firstname, record.user.middlename, record.user.lastname].filter(Boolean).join(' ') ||
              record.user.username
            }
          />
          <FunctionField<UserCallRecordingConf>
            label='resources.user_call_recording_confs.fields.type'
            source={UserCallRecordingConfFields.type}
            render={(record) =>
              translate(`resources.call_recording_confs.fields.types.${record.type}`, { _: record.type })
            }
          />
          <WrapperField label={false}>
            <EditTypeButton nestedResource={resource} />
            <DeleteWithConfirmButton
              redirect={false}
              resource={resource}
              label=''
              confirmTitle='resources.user_call_recording_confs.confirmDelete.title'
              confirmContent='resources.user_call_recording_confs.confirmDelete.content'
            />
          </WrapperField>
        </OrdDatagrid>
      </OrdList>
    </ResourceContextProvider>
  );
};

export default UserCallRecordingConfsEmbeddedList;
